import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { PageChannelDto } from '@mpr/shared';

import { api } from '../lib/api.js';
import { useI18n } from '../i18n/LanguageProvider.js';
import { Icon } from './Icons.js';

/**
 * The Meta services on one Page, as a row of chips.
 *
 * A Page is not one thing — it may carry an Instagram account, a WhatsApp
 * number, and two separate inboxes — and until now the answer to "what is
 * actually connected here?" was spread over four screens.
 *
 * Only what is live, or live-but-not-quite, is shown. A service this install
 * does not use at all would be noise on every row of every Page forever, so OFF
 * is omitted; NOT_LINKED and NOT_GRANTED are kept, because those are the two an
 * operator can act on, and they need to be told apart — one is fixed at Meta or
 * on the WhatsApp screen, the other by reconnecting.
 */
function Channels({ channels }: { channels: PageChannelDto[] }) {
  const { t } = useI18n();
  const shown = channels.filter((channel) => channel.state !== 'OFF');

  if (shown.length === 0) return null;

  return (
    <div className="chan-row">
      {shown.map((channel) => (
        <span
          key={channel.key}
          className={`chan-chip chan-${channel.state.toLowerCase()}`}
          title={t(`channel.state.${channel.state}` as never)}
        >
          {t(`channel.${channel.key}` as never)}
          {channel.detail && (
            <span className="chan-detail" dir="ltr">
              {channel.detail}
            </span>
          )}
        </span>
      ))}
    </div>
  );
}

export function PagesPanel() {
  const queryClient = useQueryClient();
  const { t } = useI18n();
  const pages = useQuery({ queryKey: ['pages'], queryFn: api.pages });

  const sync = useMutation({
    mutationFn: api.syncPages,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['pages'] }),
  });

  if (pages.isLoading) return <div className="loading">{t('common.loading')}</div>;
  if (pages.isError) return <div className="notice error">{t('common.requestFailed')}</div>;

  const rows = pages.data?.pages ?? [];

  return (
    <section className="panel">
      <div className="panel-head">
        <h2>{t('pages.title')}</h2>
        <button type="button" onClick={() => sync.mutate()} disabled={sync.isPending}>
          <Icon.refresh />
          {sync.isPending ? t('pages.syncing') : t('pages.sync')}
        </button>
      </div>

      <p className="muted small">{t('pages.subtitle')}</p>

      {rows.length === 0 ? (
        <div className="empty-state">
          <Icon.pages />
          <p>{t('pages.empty')}</p>
        </div>
      ) : (
        <div className="grid-wrap">
          <table className="grid">
            <thead>
              <tr>
                <th>{t('common.page')}</th>
                <th>{t('pages.pageId')}</th>
                <th>{t('pages.category')}</th>
                <th>{t('pages.tasks')}</th>
                <th>{t('pages.canPublish')}</th>
                <th>{t('pages.canComment')}</th>
                <th>{t('pages.token')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((page) => (
                <tr key={page.id} className={page.canPublish ? '' : 'dim'}>
                  {/* The row's name, so a phone gives it the whole card. */}
                  <td data-primary>
                    <strong>{page.name}</strong>
                    <Channels channels={page.channels} />
                  </td>
                  <td className="mono small">{page.externalId}</td>
                  <td className="small">{page.category ?? '—'}</td>
                  <td className="mono tiny">{page.tasks.join(', ') || '—'}</td>
                  <td>
                    <span className={`badge ${page.canPublish ? 'ok' : 'warn'}`}>
                      {page.canPublish ? t('common.yes') : t('common.no')}
                    </span>
                  </td>
                  <td>
                    <span className={`badge ${page.canComment ? 'ok' : ''}`}>
                      {page.canComment ? t('common.yes') : t('common.no')}
                    </span>
                  </td>
                  <td className="small">
                    {page.hasPageToken ? (
                      <span className="badge ok">{t('connections.tokenEncrypted')}</span>
                    ) : (
                      <span className="warn">
                        {page.tokenInvalidatedAt
                          ? t('pages.tokenInvalidated')
                          : t('pages.tokenMissing')}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
